import { useEffect, useMemo, useState } from 'react';
import {
  lineItemHasWork,
  trackerLabelSchema,
  type TrackerLabel,
  type DurationUnit,
  type Metafield,
  type MetafieldValue,
  type Task,
  type TaskComment,
  type TaskDependency,
  type TaskLink,
  type TaskPriority,
  type TaskStatus,
  type WorkspaceMember,
  type LineItem,
  type Worklog,
  remoteIssueUrl,
  type TaskIssueLink,
} from '@forecastify/shared';
import {
  Avatar,
  Badge,
  Button,
  Icon,
  Select,
  Tabs,
  TextInput,
  Tooltip,
  useIsMobile,
  TruncatedText,
} from '@forecastify/ui';
import { useDuration } from '../../duration';
import { DurationUnitPicker, useDurationIn } from '../../duration-input';
import { useT } from '../../i18n';
import { useSession } from '../../session';
import { useAvatars } from '../use-avatar';
import { TaskPicker } from './task-picker';
import { DetailBox, DetailRow, DetailValue, INLINE_INPUT } from '../card';
import { MetafieldForm } from '../metafields/metafield-form';
import { COMMENT_PROSE, CommentComposer } from './comment-composer';
import { Attachments } from '../attachments';
import { ScopedDropSurface, dropZone } from '../scoped-drop';
import { escapeHtml } from '../escape-html';
import { TaskLogs } from './task-logs';
import type { WorklogDraft } from '../time/worklog-form';
import { DateField } from '../date-field';
import { lineItemOptionLabel, repeatedContractNames } from '../contracts/contract-label';
import { EntityHistory } from '../history/entity-history';
import { ProviderLogo } from '../integrations/provider-logo';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ The task detail BODY. One component; three containers wrap it.            │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THE TRI-MODAL DETAIL IS ONE COMPONENT AND A `wide` FLAG — that is the whole mechanism.**
 * The mockup does exactly this: `<TaskDetail data={taskData} wide={…}/>` appears three times, in a
 * `Sheet`, in a bare `Dialog` and on a page. Three copies of a screen this size is three places for
 * a field to be added to two of them, and the drawer would be the one that quietly falls behind.
 *
 * The layout classes are the mockup's own, measured out of `TaskDetail.jsx` rather than chosen:
 *
 *  * wide — `grid grid-cols-[minmax(0,1fr)_308px] items-start gap-6 p-5`, with the sidebar spanning
 *    both rows so the activity pane sits under the main column rather than under the sidebar;
 *  * narrow — `flex flex-col gap-6 p-5`, main / sidebar / activity in that order.
 *
 * ⚠️ **Every edit saves ONE field**, which is what the API's `PATCH` is shaped for: a status pick
 * sends `{ statusId, version }` and nothing else. See `updateTaskRequestSchema`.
 */

/**
 * Paint the mentions in a comment.
 *
 * ⚠️⚠️ **THE MENTION IS PLAIN TEXT IN THE BODY, so the highlight is a text match — and that is the
 * cost `0026` chose knowingly.** The sanitiser drops every attribute, which is what makes
 * `dangerouslySetInnerHTML` above safe; carrying the member id inside the HTML would mean widening
 * it, and its own header says a regex sanitiser stops being adequate the moment it keeps attributes.
 * So the authoritative half of a mention is `comment.mentions` (the stored reference, which notified
 * the right person and survives a rename) and this is the cosmetic half.
 *
 * ⚠️ **A rename therefore un-paints an old mention.** The sentence still reads as it was written,
 * which is arguably correct for a quotation, and the reference is unaffected. Stated so the next
 * person finds it written down rather than as a bug.
 *
 * ⚠️⚠️ **THE NAME IS ESCAPED TWICE OVER, and both are load-bearing.** It goes into a RegExp, so a
 * name containing `.` or `(` — *"Kovács A. (Anna)"* is a plausible display name — would otherwise be
 * a pattern rather than a literal. And it is inserted into HTML, so it is escaped for that too; the
 * body is already-sanitised HTML where `&` is `&amp;`, and matching against the raw name would
 * silently fail for anybody with an ampersand while a *substitution* built from the raw name would
 * put unescaped text back into the document.
 */
export function highlightMentions(comment: TaskComment): string {
  if (comment.mentions.length === 0) return comment.bodyHtml;

  let html = comment.bodyHtml;
  for (const mention of comment.mentions) {
    const escaped = mention.displayName
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    const pattern = new RegExp(`@${escaped.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`, 'g');
    /* ⚠️ `<b>`, not a `<span class>`: the eleven tags the server allows are the vocabulary a comment
     * body speaks, and the `[&_b]` rule on the container above styles this one occurrence without
     * teaching the renderer a second one. */
    html = html.replace(pattern, `<b>@${escaped}</b>`);
  }
  return html;
}

export interface TaskDetailData {
  readonly task: Task;
  readonly statuses: readonly TaskStatus[];
  readonly priorities: readonly TaskPriority[];
  readonly assignees: readonly { id: string; name: string }[];
  /** C17. The workspace's members, for the mention picker — see the note in `task-route.tsx`. */
  readonly people: readonly WorkspaceMember[];
  readonly projects: readonly { id: string; name: string }[];
  /** C25 · the workspace's line items, for the picker. Empty when it has none — see the field. */
  readonly lineItems: readonly LineItem[];
  readonly subtasks: readonly Task[];
  readonly links: readonly TaskLink[];
  readonly dependencies: readonly TaskDependency[];
  readonly comments: readonly TaskComment[];
  /**
   * ⚠️ **Who is looking, so the comment controls can be offered only on their OWN.** The server
   * refuses anybody else's (`NotYourCommentError`), and an edit button that always ends in a
   * refusal teaches people the product is broken rather than that the rule exists.
   */
  readonly currentMemberId?: string | null;
  /** C10. The task-entity metafields, and this task's values for them. */
  readonly metafields: readonly Metafield[];
  /** C36 · the remote issue this task came from, or `null` — most tasks have none. */
  readonly issueLink: TaskIssueLink | null;
  /** C11. The task's own worklogs — one page of them, newest day first. */
  readonly worklogs: readonly Worklog[];
  /** C12. Whether `/worklogs` offered another page. Shown, not swallowed. */
  readonly worklogsPartial: boolean;
}

export interface TaskDetailHandlers {
  readonly onPatch: (patch: Record<string, unknown>) => void;
  /**
   * ⚠️ Item 73 · editing and deleting a comment. Optional together, so every embedded copy of this
   * detail and every test keeps working without them.
   */
  readonly onEditComment?: (
    comment: TaskComment,
    html: string,
    mentionMemberIds: readonly string[],
  ) => void;
  readonly onDeleteComment?: (comment: TaskComment) => void;
  readonly onAddComment: (
    html: string,
    mentionMemberIds: readonly string[],
    files?: readonly File[],
  ) => void;
  readonly onOpenTask: (task: Task) => void;
  /**
   * ⚠️ **A file dropped on the surface, for the task's own folder.** Resolves when it has landed,
   * so the attachment list can re-read — it owns its own state and has no other way to hear.
   * Absent for a reader who may not edit; the surface is then inert.
   */
  readonly onAttachFiles?: (files: readonly File[]) => Promise<void>;
  /**
   * ⚠️ A file dropped ON THE DESCRIPTION. It attaches to the task and appends a link to the
   * description's HTML — `task-route.tsx` does both, because it has the version and the workspace
   * path and this component has neither.
   */
  readonly onAttachToDescription?: (files: readonly File[], html: string | null) => Promise<void>;
  /**
   * ⚠️ **The chip's ×.** It deletes the FILE and takes its anchor out of the description in one
   * act, because in this model they are one thing: a file in the description lives there and
   * nowhere else (`0117`), so removing the mention is removing the file.
   */
  readonly onRemoveDescriptionFile?: (fileId: string, html: string | null) => void;
  /** C11. Logging against this task, and removing an entry. */
  /** ⚠️ The whole draft since feedback round 4 item 20 — `worklog-form.tsx` says why. */
  readonly onLogTime: (draft: WorklogDraft) => void;
  /**
   * ⚠️ Whether the workspace defines any active overtime type — the form's marking control is
   * absent when it does not. The TYPES themselves are the approver's business since `0147`.
   */
  readonly overtimeOffered?: boolean;
  readonly onDeleteWorklog: (entry: Worklog) => void;
  readonly canEdit: boolean;
  /** `worklog.create` and `worklog.update`. UI gating is UX; the server checks again. */
  readonly canLogTime: boolean;
  readonly canEditWorklog: boolean;
  /**
   * Relations. See `useTaskWrites` for what each one writes.
   *
   * ⚠️ **`canSchedule` is a SEPARATE action from `canEdit`.** Links need `task.update`;
   * dependencies need `task.schedule`, which the permission catalogue calls *"Ütemezés és
   * függőségek"* and which PM holds but Alvállalkozó does not. Gating both on one flag would hand
   * the timeline to everybody who can rename a task.
   */
  readonly canSchedule: boolean;
  readonly onCreateSubtask: (title: string) => void;
  readonly onDetachSubtask: (subtask: Task) => void;
  readonly onAddLink: (otherTaskId: string) => void;
  readonly onRemoveLink: (linkId: string) => void;
  readonly onAddDependency: (predecessorId: string, lagDays: number) => void;
  readonly onRemoveDependency: (dependencyId: string) => void;
  /**
   * Finding the other task, for a link or a predecessor.
   *
   * ⚠️ **A HANDLER, NOT A FETCH IN THE PICKER.** The mockup hands its dropdown every task in the
   * workspace; ours asks the server, and the request belongs to the route — a screen that reaches
   * for the session is a screen whose tests need one. `projects-screen.tsx` records what that cost
   * the last time.
   */
  readonly onSearchTasks: (query: string) => Promise<readonly Task[]>;
  readonly error: string | null;
}

export function TaskDetail({
  data,
  handlers,
  wide: wideAsked,
}: {
  readonly data: TaskDetailData;
  readonly handlers: TaskDetailHandlers;
  readonly wide: boolean;
}) {
  /* ⚠️ 17 Oct 2026 · never side by side on a phone: main first, the properties under it. */
  const mobile = useIsMobile();
  const wide = wideAsked && !mobile;
  const t = useT();
  const [tab, setTab] = useState('comments');
  const { task } = data;
  /* ⚠️ Bumped after an upload so `Attachments` re-reads — it owns its own list and has no other
     way to hear that something landed in it from outside. */
  const [uploads, setUploads] = useState(0);
  /* ⚠️ Handed to the composer as its starting attachments. Cleared once it has them, so a second
     drop is a second stage rather than an append to a list nobody can see. */
  const [stagedFiles, setStagedFiles] = useState<readonly File[] | null>(null);

  return (
    /**
     * ┌───────────────────────────────────────────────────────────────────────┐
     * │ ⚠️⚠️ THE WHOLE SCREEN TAKES A FILE — 14 Sep, copied from Jira.        │
     * └───────────────────────────────────────────────────────────────────────┘
     *
     * Drop anywhere and it becomes the task's attachment; drop over the description or the comment
     * box and it goes there instead. `scoped-drop.tsx` carries how the target is resolved and why
     * it is read from the pointer rather than from registered regions.
     *
     * ⚠️ **A comment drop OPENS the composer with the file staged** rather than posting a bare
     * comment with an attachment and no words. The file is what somebody is explaining; posting it
     * alone would make the explanation a second comment.
     */
    <ScopedDropSurface
      entityLabel={task.key}
      enabled={handlers.canEdit}
      /**
       * ┌───────────────────────────────────────────────────────────────────────────┐
       * │ ⚠️⚠️ THREE DESTINATIONS, AND EACH ONE IS HONEST ABOUT WHAT IT DOES.       │
       * └───────────────────────────────────────────────────────────────────────────┘
       *
       * The surface attaches to the TASK; the comment box opens the composer with the file staged;
       * the description attaches to the task **and writes a link into the text**.
       *
       * ⚠️⚠️ **THE DESCRIPTION ZONE WAS REMOVED ON 15 Sep AND PUT BACK THE SAME DAY, AND THE
       * REMOVAL WAS THE WRONG CALL.** It was reported as dropping to the wrong place; it was, and
       * the veil said *Leírás* while doing it, so the fix looked like *stop promising that*. What
       * had actually been asked for was the promise KEPT. The obstacle was real — a link needs an
       * address and every file here sits behind an authenticated fetch — and an obstacle is not a
       * reason to delete a feature somebody asked for twice. `files/file-route.tsx` is the address.
       */
      zoneLabels={{
        description: t('task.field.description'),
        comment: t('task.tab.comments'),
      }}
      onFiles={(files, zone) => {
        if (zone === 'comment') {
          setTab('comments');
          setStagedFiles(files);
          return;
        }
        if (zone === 'description' && handlers.onAttachToDescription !== undefined) {
          /* ⚠️ The CURRENT html, so the link is appended to what is on screen rather than to
             whatever the route last fetched. */
          void handlers.onAttachToDescription(files, task.description).then(() => {
            setUploads((count) => count + 1);
          });
          return;
        }
        /* ⚠️ Everything else is the task's folder — see `zoneLabels`. */
        void handlers.onAttachFiles?.(files).then(() => {
          setUploads((count) => count + 1);
        });
      }}
      className={wide ? 'relative flex min-h-0 flex-1 flex-col' : 'relative flex flex-col'}
    >
      {/**
       * ┌───────────────────────────────────────────────────────────────────────┐
       * │ ⚠️⚠️ THE TITLE STAYS, AND THE SIDEBAR SCROLLS ON ITS OWN — 14 Oct.    │
       * └───────────────────────────────────────────────────────────────────────┘
       *
       * The headline sat at the top of the MAIN column, inside the one scroll region the whole
       * detail was, so a long comment thread took the title away and the sidebar went with it. It
       * is the frame's header now — opaque, ruled, spanning both columns — and in the wide frame the
       * body and the sidebar are two scrollers side by side. `DetailFrame` in `detail-chrome.tsx`
       * is the same shape for the contract details, and the argument for each class is there.
       *
       * ⚠️ **`pt-1` under the header, not `pt-5`**: the description already brings its own `mt-4`.
       */}
      <div data-task-detail={task.key} className="contents">
        <div className="bg-background sticky top-0 z-10 shrink-0 border-b px-5 pt-5 pb-4">
          <Headline task={task} handlers={handlers} />
        </div>

        <div className={wide ? 'flex min-h-0 flex-1' : 'flex flex-col gap-6 px-5 pt-1 pb-5'}>
          <div
            className={
              wide
                ? 'flex min-w-0 flex-1 flex-col gap-6 overflow-y-auto pt-1 pr-3 pb-5 pl-5'
                : 'contents'
            }
          >
            <div className={wide ? '' : 'order-1'}>
              <Description task={task} handlers={handlers} />
              <Subtasks data={data} handlers={handlers} />
              <Relations data={data} handlers={handlers} />
            </div>

            {/**
             * ⚠️⚠️ **THE METAFIELDS MOVED INTO THE SIDEBAR — 14 Sep.** They were a box of their own in the
             * main column, under the description and above the activity, with a Save button.
             *
             * A metafield is a PROPERTY of the task — a workspace's own column beside the product's —
             * and the rail is where properties live. Putting them in the main column made them read as
             * content, which is what the description and the subtasks are; and the Save button made them
             * the only thing on the screen that had to be committed, when the assignee, the status and
             * the dates beside them all save as you set them.
             *
             * ⚠️ **The attachments take the row the box used to occupy**, which is where the mockup
             * draws them: a folder belonging to the ticket, between what it is and the talk about it.
             */}
            <div className={wide ? '' : 'order-3'} data-tour="task-attachments">
              <TaskAttachments data={data} handlers={handlers} uploads={uploads} />
            </div>

            <section className={wide ? '' : 'order-4'} data-tour="task-activity">
              <Tabs
                variant="underline"
                label={t('task.activity')}
                value={tab}
                onValueChange={setTab}
                items={[
                  { value: 'comments', label: t('task.tab.comments'), icon: 'message-square' },
                  { value: 'history', label: t('history.title'), icon: 'clock' },
                  /* C11 pays C8's debt. `timer` is the mockup's own icon for it. */
                  {
                    value: 'logs',
                    label: t('task.tab.logs'),
                    icon: 'timer',
                    count: data.worklogs.length,
                  },
                ]}
              />

              {tab === 'comments' ? (
                <Comments
                  data={data}
                  handlers={handlers}
                  stagedFiles={stagedFiles}
                  onStagedTaken={() => {
                    setStagedFiles(null);
                  }}
                />
              ) : tab === 'logs' ? (
                <TaskLogs
                  entries={data.worklogs}
                  /* ⚠️ The aggregate, not a sum of the page. See `TaskLogs`. */
                  trackedHours={task.trackedHours}
                  partial={data.worklogsPartial}
                  canLog={handlers.canLogTime}
                  canEdit={handlers.canEditWorklog}
                  onLog={handlers.onLogTime}
                  overtimeOffered={handlers.overtimeOffered ?? false}
                  onDelete={handlers.onDeleteWorklog}
                />
              ) : (
                /* ux-pass-2 · the one history (`screens/history/`), read when the tab opens. */
                <div className="mt-3">
                  <EntityHistory type="task" id={task.id} />
                </div>
              )}
            </section>

            {handlers.error === null ? null : (
              <p className={`text-destructive text-[12px] ${wide ? '' : 'order-4'}`} role="alert">
                {handlers.error}
              </p>
            )}
          </div>

          <aside className={wide ? 'w-[308px] shrink-0 overflow-y-auto py-5 pr-5 pl-3' : 'order-2'}>
            <Sidebar data={data} handlers={handlers} />
          </aside>
        </div>
      </div>
    </ScopedDropSurface>
  );
}

/* ── the title, edited in place ────────────────────────────────────────────────────────────── */

/**
 * ⚠️ **Explicit save; blur does NOT commit.** The design system spells the inline-edit contract
 * out, and blur-saves is the clause it names first: clicking away to check something else must not
 * write. Enter saves, Escape cancels, ✓ and ✕ do the same with a pointer.
 *
 * ⚠️ **The editor holds its own DRAFT string.** Deriving the input's value from the saved task
 * makes intermediate states unrepresentable — the design system records a tag editor that made a
 * comma impossible to type for exactly this reason, with every test passing.
 */
function Headline({
  task,
  handlers,
}: {
  readonly task: Task;
  readonly handlers: TaskDetailHandlers;
}) {
  const t = useT();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');

  if (editing) {
    return (
      <div className="flex items-start gap-2">
        <TextInput
          value={draft}
          autoFocus
          aria-label={t('task.field.title')}
          onChange={(event) => {
            setDraft(event.target.value);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              handlers.onPatch({ title: draft.trim() });
              setEditing(false);
            }
            if (event.key === 'Escape') {
              /* Handled — the modal around it stays open (`useModalSurface`). */
              event.preventDefault();
              setEditing(false);
            }
          }}
        />
        <Button
          size="icon"
          aria-label={t('common.save')}
          onClick={() => {
            handlers.onPatch({ title: draft.trim() });
            setEditing(false);
          }}
        >
          <Icon name="check" size={14} />
        </Button>
        <Button
          variant="outline"
          size="icon"
          aria-label={t('common.cancel')}
          onClick={() => {
            setEditing(false);
          }}
        >
          <Icon name="x" size={14} />
        </Button>
      </div>
    );
  }

  return (
    /**
     * ┌───────────────────────────────────────────────────────────────────────┐
     * │ ⚠️⚠️ CLICK THE TITLE, NOT A PENCIL — item 74.                         │
     * └───────────────────────────────────────────────────────────────────────┘
     *
     * *"Inline edit — click the field, not a pencil; match the contract's rename."* The contract
     * and the line item have worked this way since C25 (`InlineTitle` in `detail-chrome.tsx`); the
     * task kept a pencil beside its heading, so the same gesture opened an editor on two surfaces
     * and did nothing on the third.
     *
     * ⚠️⚠️ **A REAL `<button>` WRAPPING THE HEADING, NOT AN `onClick` ON THE `<h2>`.** A click
     * handler on a heading is unreachable by keyboard and announces nothing — this is a control,
     * and the only correct markup for a control is one. The heading stays INSIDE it so the
     * document outline is unchanged.
     *
     * ⚠️ **`title` carries the affordance** that the pencil used to carry visibly. A field that
     * only reveals itself on hover is why the hover background is here as well.
     *
     * ⚠️ **The KEY is gone from beside the title — 14 Sep.** It is the last segment of the header
     * breadcrumb now, with the copy button that makes it useful. Here it was a second printing of
     * the same six characters, indented from a heading it was not part of.
     */
    <div className="flex items-start gap-2">
      {handlers.canEdit ? (
        <Tooltip asChild content={t('task.action.renameTitle')}>
          <button
            type="button"
            data-tour="task-title"
            className="hover:bg-accent/40 -mx-1.5 min-w-0 flex-1 rounded-md px-1.5 py-0.5 text-left"
            onClick={() => {
              setDraft(task.title);
              setEditing(true);
            }}
          >
            <h2 className="text-[17px] leading-snug font-semibold">{task.title}</h2>
          </button>
        </Tooltip>
      ) : (
        <h2 className="min-w-0 flex-1 text-[17px] leading-snug font-semibold">{task.title}</h2>
      )}
    </div>
  );
}

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ INLINE, AND THE COMMENTS' OWN EDITOR — 14 Sep.                       │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * It was a plain `Textarea` behind an *Edit* button, with Save and Cancel under it: three clicks
 * to fix a typo, and no formatting at all in the one field on the screen most likely to want a
 * list or a code fence. Meanwhile the comment box two sections down had all of it.
 *
 * ⚠️ **Click the text to edit, like the title.** The whole block is the affordance — the hover
 * background says so — rather than a button beside it that somebody has to find.
 *
 * ⚠️⚠️ **`CommentComposer`, NOT A SECOND EDITOR.** Everything it carries is wanted here: the
 * toolbar, the paste handling, the keyboard shortcuts. A second rich-text field would be a second
 * place for all of that to drift, and the first thing to drift is always the paste path.
 *
 * ⚠️ **`allowFiles={false}` and no `people`.** A task's files belong to the ticket's own folder,
 * which is a section of its own now, and an `@` in a description notifies nobody — the mention
 * machinery is the comment thread's. Offering either here would be two affordances that look like
 * the thread's and behave differently.
 *
 * ⚠️⚠️ **THE HTML IS RENDERED WITH `dangerouslySetInnerHTML`, AND THAT IS SAFE FOR ONE REASON:
 * `task.service.ts` SANITISES ON WRITE.** The same allow-list the comments go through, at both
 * write paths. If that ever weakens this line becomes stored XSS — the sanitiser's own tests name
 * the attacks it stops.
 */
function Description({
  task,
  handlers,
}: {
  readonly task: Task;
  readonly handlers: TaskDetailHandlers;
}) {
  const t = useT();
  const [editing, setEditing] = useState(false);
  const { prose, files } = splitDescription(task.description);

  const chips = (
    <FileChips
      files={files}
      {...(handlers.onRemoveDescriptionFile === undefined
        ? {}
        : {
            onRemove: (fileId: string) => {
              handlers.onRemoveDescriptionFile?.(fileId, task.description);
            },
          })}
    />
  );

  if (editing) {
    return (
      <div className="mt-4">
        {/**
         * ⚠️ `commitOnBlur` — item 3, second pass: it saves when focus leaves, like every other
         * inline editor on this screen. `comment-composer.tsx` argues the focus test.
         *
         * ⚠️⚠️ **AND `footer`, WHICH IS FEEDBACK ROUND 4 ITEM 13.** The same second pass took the
         * Save button away, and the round asked for it back: a description is the one inline field
         * somebody writes paragraphs into, so *"did that save?"* is a real question here in a way it
         * is not for a one-line title or a picker. Both now — **Mentés** and **Mégse** like the
         * title editor above, and an outside click still puts the editor away, unchanged or not.
         */}
        <CommentComposer
          /**
           * ┌───────────────────────────────────────────────────────────────────────────┐
           * │ ⚠️⚠️ THE PROSE ONLY — THE FILES ARE NOT IN THE EDITOR. 15 Sep.            │
           * └───────────────────────────────────────────────────────────────────────────┘
           *
           * Handing the whole description to TipTap turned every chip back into a link the moment
           * somebody clicked into the text, because a chip is a thing this renderer draws and the
           * editor knows only anchors. The alternative is teaching TipTap a custom node with its own
           * schema, parser, serialiser and NodeView — a lot of machinery so that an editor can draw
           * something nobody edits.
           *
           * ⚠️ **So the files stay OUTSIDE the editor in both modes**, as the same chips in the same
           * place, and the save puts their anchors back. What somebody edits is the words; the files
           * are not text and behave like attachments throughout.
           */
          initialHtml={prose}
          allowFiles={false}
          commitOnBlur
          footer
          submitLabel={t('common.save')}
          onCancel={() => {
            setEditing(false);
          }}
          onSubmit={(html) => {
            /**
             * ⚠️ **The anchors are re-appended from the CURRENT files**, which is the list this
             * render was drawn from — so a chip deleted while the editor was open is already gone
             * from it and does not come back on save.
             */
            const kept = files.map(anchorFor).join('');
            /* ⚠️ An empty editor clears the field rather than storing `<p></p>`, which would
               render as a blank line nobody can select or delete. */
            const stripped = html.replace(/<[^>]*>/gu, '').trim();
            const body = stripped === '' ? kept : `${html}${kept}`;
            handlers.onPatch({ description: body === '' ? null : body });
            setEditing(false);
          }}
        />
        {chips}
      </div>
    );
  }

  if (!handlers.canEdit) {
    return (
      <div className="mt-4">
        {task.description === null ? (
          <p className="text-muted-foreground text-[13px]">{t('task.description.empty')}</p>
        ) : (
          <>
            <div
              className={`text-[13px] leading-relaxed ${COMMENT_PROSE}`}
              dangerouslySetInnerHTML={{ __html: prose }}
            />
            {/* ⚠️ Without the ×: a reader who may not edit may not delete either. */}
            <FileChips files={files} />
          </>
        )}
      </div>
    );
  }

  return (
    /**
     * ⚠️⚠️ **A `div` WITH A CLICK, NOT A `button`, AND THE CHIPS ARE WHY.** A chip carries its own
     * × — a button — and HTML forbids a button inside a button: React renders it, the browser
     * silently reparents it out of its parent, and the × lands beside the description instead of in
     * the chip. `onClick` on a div needs the keyboard path putting back by hand, which is what
     * `role`, `tabIndex` and the Enter/Space handler below are.
     */
    <Tooltip asChild content={t('task.action.editDescription')}>
      <div
        role="button"
        tabIndex={0}
        data-tour="task-description"
        /* ⚠️ A drop zone even while reading: the gesture is about where the file goes, not about
         whether the field happens to be open for editing. */
        {...dropZone('description')}
        className="hover:bg-accent/40 focus-visible:ring-ring/50 -mx-1.5 mt-4 block w-full cursor-text rounded-md px-1.5 py-1 text-left transition-colors focus-visible:ring-2 focus-visible:outline-none"
        onClick={() => {
          setEditing(true);
        }}
        onKeyDown={(event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          setEditing(true);
        }}
      >
        {task.description === null ? (
          <span className="text-muted-foreground text-[13px]">{t('task.description.empty')}</span>
        ) : (
          <div
            className={`text-[13px] leading-relaxed ${COMMENT_PROSE}`}
            dangerouslySetInnerHTML={{ __html: prose }}
          />
        )}

        {chips}
      </div>
    </Tooltip>
  );
}

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ THE FILES IN A DESCRIPTION, AS CHIPS. Ruled on 15 Sep.               │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * *"The attachment should only appear as a chip (not a link) inside the description… the chip has an
 * x to delete it."*
 *
 * ⚠️⚠️ **STORED AS AN ANCHOR, DRAWN AS A CHIP, AND THE STORAGE IS NOT NEGOTIABLE.** `sanitiseHtml`
 * allows `href`, `rel` and `target` and strips everything else, so a `<span data-file=…>` comes back
 * from the server as bare text. An anchor survives, carries the id in its `href`, and degrades to a
 * working link anywhere the chip renderer is not — an export, a notification, a future read-only
 * view.
 *
 * ⚠️ **The × is a real button and the chip is a real link**, so the two gestures are distinct to a
 * keyboard and to assistive technology. `stopPropagation` on both, or clicking either would also
 * open the editor underneath.
 */
function FileChips({
  files,
  onRemove,
}: {
  readonly files: readonly { id: string; name: string; href: string }[];
  readonly onRemove?: (fileId: string) => void;
}) {
  const t = useT();
  if (files.length === 0) return null;

  return (
    <div className="mt-2 flex flex-wrap gap-1.5">
      {files.map((file) => (
        <span
          key={file.id}
          className="bg-muted flex max-w-[260px] items-center gap-1.5 rounded-md border px-2 py-1 text-[11.5px]"
        >
          <Icon name="paperclip" size={12} className="text-muted-foreground shrink-0" />
          <Tooltip asChild overflowOnly content={file.name}>
            <a
              href={file.href}
              target="_blank"
              rel="noopener noreferrer"
              className="truncate"
              onClick={(event) => {
                event.stopPropagation();
              }}
            >
              {file.name}
            </a>
          </Tooltip>
          {onRemove === undefined ? null : (
            <button
              type="button"
              aria-label={t('task.attachment.remove', { name: file.name })}
              className="text-muted-foreground hover:text-foreground shrink-0"
              /**
               * ⚠️⚠️ **`preventDefault` ON MOUSEDOWN, AND WITHOUT IT THE EDITOR SAVES FIRST.** The
               * chips sit outside the composer, so pressing one moves focus out of it — and the
               * description commits on blur (item 3). The order would be: blur, save the prose,
               * THEN delete the file, which is two writes racing each other over one version.
               * Preventing the default on mousedown keeps focus where it is, so the click is the
               * only thing that happens.
               */
              onMouseDown={(event) => {
                event.preventDefault();
              }}
              onClick={(event) => {
                event.stopPropagation();
                onRemove(file.id);
              }}
            >
              <Icon name="x" size={12} />
            </button>
          )}
        </span>
      ))}
    </div>
  );
}

/**
 * One file, back as the anchor it is stored as.
 *
 * ⚠️ **The href is the STORED one**, not rebuilt from the id: it is workspace-scoped and this
 * component does not know the workspace. Round-tripping what the server sent is also the only
 * version that cannot drift from what the drop wrote.
 *
 * ⚠️ **The name is escaped**, for `escape-html.ts`'s reason — it came from an uploader, and it is
 * about to become markup for the second time.
 */
function anchorFor(file: { name: string; href: string }): string {
  return `<p><a href="${escapeHtml(file.href)}">${escapeHtml(file.name)}</a></p>`;
}

/**
 * The prose and the files, out of one stored description.
 *
 * ⚠️⚠️ **PARSED, NOT REGEXED** — an attribute containing `>` ends a regex match early and a lazy
 * `.*?` stops at the first `</a>` rather than the matching one. The browser has a parser and this
 * runs in the browser.
 *
 * ⚠️ **A file anchor is one whose href names `/files/<id>`**, which is what the drop writes. A link
 * somebody typed to a website stays in the prose, where it belongs — the two are not the same thing
 * and must not be drawn the same way.
 *
 * ⚠️ **The emptied paragraph goes with the anchor**, because `<p><a>…</a></p>` is exactly what the
 * drop writes and a blank line nobody can select is what would be left.
 */
function splitDescription(html: string | null): {
  prose: string;
  files: { id: string; name: string; href: string }[];
} {
  if (html === null || html === '') return { prose: '', files: [] };

  const parsed = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html');
  const files: { id: string; name: string; href: string }[] = [];

  for (const anchor of parsed.querySelectorAll('a[href*="/files/"]')) {
    const href = anchor.getAttribute('href') ?? '';
    const id = /\/files\/([^/?#]+)/u.exec(href)?.[1];
    if (id === undefined) continue;

    files.push({ id, name: anchor.textContent.trim(), href });
    const paragraph = anchor.closest('p');
    anchor.remove();
    if (paragraph !== null && paragraph.textContent.trim() === '') paragraph.remove();
  }

  return { prose: parsed.body.innerHTML, files };
}

/* ── the sidebar: every one-field save ─────────────────────────────────────────────────────── */

function Sidebar({
  data,
  handlers,
}: {
  readonly data: TaskDetailData;
  readonly handlers: TaskDetailHandlers;
}) {
  const t = useT();
  const { task } = data;

  /**
   * ⚠️⚠️ **ONLY THE LINE ITEMS ON THIS TASK'S PROJECT.** A line item reaches a project through its
   * contract, and the server refuses a cross-project assignment with `line_item_not_on_project` —
   * so offering the whole workspace would be a picker whose entries mostly produce a 409. A task
   * with no project has nothing legal to offer, which is why the empty case hides the row.
   */
  /**
   * ⚠️ P2-32.2 · every line item that can hold work, on any project: choosing one on another project
   * MOVES the task there, because a task's project is its line item's. A line item with no project
   * (a client contract's licence) cannot hold a task and is not offered.
   */
  /* ⚠️ U-7 · nor is a licence, which the server refuses with `line_item_takes_no_work` — except the
     one the task already sits on, which predates the rule and must still render as its value. */
  const onProject = useMemo(
    () =>
      data.lineItems.filter(
        (item) =>
          item.projectId !== null &&
          /* IP-3 · a closed one is drawn unpickable below (`line_item_closed`). */
          (lineItemHasWork(item.type) || item.id === task.lineItemId),
      ),
    [data.lineItems, task.lineItemId],
  );

  /**
   * ⚠️⚠️ **A TITLED BOX OF ALIGNED ROWS, like every other property rail in the product.** It was an
   * untitled `p-4` card whose rows stacked a label above each control, so the whole rail read as a
   * form. `people.markup.html` 333 is the shape: a bordered title strip over a 92px label column,
   * with the controls wearing no chrome until they are used.
   */
  const issue = data.issueLink;
  const issueUrl = issue === null ? null : remoteIssueUrl(issue);

  return (
    /* ⚠️ `gap-4` between the two boxes, the same rhythm the person rail uses — item 1. */
    <div className="flex flex-col gap-4">
      <DetailBox title={t('task.properties')} tourId="task-properties">
        {/**
         * ⚠️⚠️ **FIRST, AND THE KEY COLLISION IS WHY.** Our task key and the tracker's are two
         * counters that render identically: a project prefixed `HSO` linked to a Jira space keyed
         * `HSO` makes `HSO-51` a valid identifier in both systems pointing at different work. Anybody
         * asking *"why is my hour on HSO-1358 when I logged it on HSO-51?"* is reading a screen that
         * never told them the two are the same issue — so it goes above the properties they came for.
         */}
        {/**
         * J-7 · the key is Forecastify's provisional one while the tracker makes the issue; the
         * tracker's replaces it when the create answers, and the live topic redraws this.
         */}
        {task.keyPending ? (
          <Row label={t('task.field.key')}>
            <span className="flex min-w-0 items-center gap-1.5 px-2 text-[12px]">
              <span className="tabular-nums">{task.key}</span>
              <span className="text-muted-foreground shrink-0 truncate text-[11.5px]">
                {t('task.key.pending')}
              </span>
            </span>
          </Row>
        ) : null}
        {issue === null ? null : (
          <Row label={t('task.field.remoteIssue')}>
            {/* ⚠️ `px-2`, so the key starts where every other value's text does. */}
            <span className="flex min-w-0 items-center gap-1.5 px-2 text-[12px]">
              {/* ⚠️ The tracker's mark, with the instance's name on hover — a long instance name
                  printed beside the key truncated the key itself, which is what the row is for. */}
              <Tooltip asChild content={issue.instanceName}>
                <span className="inline-flex shrink-0">
                  <ProviderLogo provider={issue.extensionId} label={issue.instanceName} />
                </span>
              </Tooltip>
              {issueUrl === null ? (
                /* ⚠️ Linked by id before any sync read it — there is a link but no key yet. */
                <span className="text-muted-foreground">{t('task.remoteIssue.pending')}</span>
              ) : (
                <a href={issueUrl} target="_blank" rel="noreferrer" className="truncate">
                  {issue.remoteKey}
                </a>
              )}
              <Icon name="link" size={12} className="text-muted-foreground shrink-0" />
            </span>
          </Row>
        )}
        {/**
         * ⚠️⚠️ **J-1 · WHAT THE TRACKER FILES IT UNDER, READ-ONLY, BESIDE THE ISSUE IT CAME FROM.**
         * Jira owns all three — the next sync replaces them — so they are drawn as chips with no
         * control, never as a field that looks editable and then forgets. A row with nothing in it is
         * left out: a YouTrack task would otherwise carry three dashes it can never fill.
         */}
        <TrackerRow label={t('task.field.components')} names={task.tracker?.components ?? []} />
        <TrackerRow label={t('task.field.fixVersions')} names={task.tracker?.fixVersions ?? []} />
        <TrackerRow
          label={t('task.field.epic')}
          names={task.tracker?.epic == null ? [] : [task.tracker.epic.name]}
        />
        <Row label={t('task.field.status')}>
          {/* ⚠️⚠️ J-2 · LOCKED, AND SAYS WHY, WHILE THE TRACKER'S STATUS HAS NO COUNTERPART. The
              server refuses the move (`task_status_unmapped`); a picker that offered it would be
              offering a 409. The sentence names the Jira status, which is what has to be mapped. */}
          <span className="flex min-w-0 flex-col">
            <Select
              inline
              value={task.statusId}
              disabled={!handlers.canEdit || task.tracker?.unmappedStatus != null}
              aria-label={t('task.field.status')}
              onChange={(event) => {
                handlers.onPatch({ statusId: event.target.value });
              }}
              options={data.statuses.map((status) => ({ value: status.id, label: status.name }))}
            />
            {task.tracker?.unmappedStatus == null ? null : (
              <span className="text-muted-foreground px-2 text-[11px] leading-snug">
                {t('task.status.unmappedHelp', { status: task.tracker.unmappedStatus })}
              </span>
            )}
          </span>
        </Row>

        <Row label={t('task.field.priority')}>
          <Select
            inline
            value={task.priorityId}
            disabled={!handlers.canEdit}
            aria-label={t('task.field.priority')}
            onChange={(event) => {
              handlers.onPatch({ priorityId: event.target.value });
            }}
            options={data.priorities.map((priority) => ({
              value: priority.id,
              label: priority.name,
            }))}
          />
        </Row>

        <Row label={t('task.field.assignee')}>
          {/**
           * ⚠️⚠️ **THE ASSIGNEE PICKER IS SCOPED TO PROJECT MEMBERS — a deliberate divergence.** The
           * mockup lists every user in `assigneeItems`; P-1 says only a member of the task's project
           * may hold its work, and the server refuses the rest. **Offering a name and then refusing
           * it is worse than not offering it**, so the list is the project's people. A task with no
           * project has no constraint, so it gets everybody — which is P-1's own conditional.
           */}
          <Select
            /* U-14 · a list of records, so it searches. */
            searchable
            searchPlaceholder={t('list.searchOptions')}
            inline
            value={task.assigneeMemberId ?? ''}
            disabled={!handlers.canEdit}
            aria-label={t('task.field.assignee')}
            onChange={(event) => {
              handlers.onPatch({
                assigneeMemberId: event.target.value === '' ? null : event.target.value,
              });
            }}
            options={[
              { value: '', label: t('common.none') },
              ...data.assignees.map((person) => ({ value: person.id, label: person.name })),
            ]}
          />
        </Row>

        {/* ⚠️ P2-32.2 · read-only: a task's project is its line item's, so it moves by moving the
            line item below. An unrouted synced issue has neither until somebody files it. */}
        <Row label={t('task.field.project')}>
          {/* ⚠️ `DetailValue`, so the text starts where every control's text does (13 Oct 2026). */}
          <DetailValue>{task.projectName ?? t('task.noProject')}</DetailValue>
        </Row>

        {/**
         * C25 · which line item this task's hours belong to.
         *
         * ⚠️⚠️ **DRAWN ONLY WHERE THE WORKSPACE HAS LINE ITEMS**, which is not the same as hiding it
         * behind the flag: a tenant WITH contracts whose workspace has none yet would get a picker
         * offering nothing, which reads as broken rather than as empty. The list arrives empty in
         * both cases, so one condition covers both — and the moment somebody creates a line item the
         * field appears on every task.
         */}
        {onProject.length === 0 ? null : (
          <Row label={t('task.field.lineItem')}>
            <Select
              data-tour="task-line-item"
              inline
              searchable
              searchPlaceholder={t('rule.searchValues')}
              /* ⚠️ The rail is 160px and an option is "contract · line item", so the list opens
                 wider than its field — as the skill picker on a person does. */
              menuWidth={380}
              value={task.lineItemId ?? ''}
              disabled={!handlers.canEdit}
              aria-label={t('task.field.lineItem')}
              onChange={(event) => {
                /* ⚠️ P2-32.2 · changeable, never clearable — there is no "none" to send. */
                if (event.target.value !== '') handlers.onPatch({ lineItemId: event.target.value });
              }}
              options={[
                /* Only an unrouted synced issue has no line item; picking one files it (P2-32.5). */
                { value: '', label: t('common.choose'), disabled: true },
                /* ⚠️ Labelled with the CONTRACT CODE as well as the name, because two contracts
                 routinely have a line item called *Havi támogatás* and the name alone would make
                 the picker a coin toss. */
                ...onProject.map((item) => {
                  /* 3 Nov 2026 · the contract's NAME, the project too only where names repeat. */
                  const label = lineItemOptionLabel(item, repeatedContractNames(onProject));
                  /* 30 Oct 2026 · a closed line item is shown and unpickable, saying so — except
                     the one the task already sits on, which must still render as its value. */
                  const closed = item.status === 'closed' && item.id !== task.lineItemId;
                  return {
                    value: item.id,
                    label: closed ? t('lineItem.option.closed', { label }) : label,
                    disabled: closed,
                  };
                }),
              ]}
            />
          </Row>
        )}

        <Row label={t('task.field.estimate')}>
          <EstimateField task={task} handlers={handlers} />
        </Row>

        <Row label={t('task.field.start')}>
          <DateField
            label={t('task.field.start')}
            value={task.startsOn}
            disabled={!handlers.canEdit}
            buttonClassName="h-7 border-transparent shadow-none"
            onChange={(startsOn) => {
              handlers.onPatch({ startsOn: startsOn === '' ? null : startsOn });
            }}
          />
        </Row>

        <Row label={t('task.field.due')}>
          <DateField
            label={t('task.field.due')}
            value={task.dueOn}
            disabled={!handlers.canEdit}
            buttonClassName="h-7 border-transparent shadow-none"
            onChange={(dueOn) => {
              handlers.onPatch({ dueOn: dueOn === '' ? null : dueOn });
            }}
          />
        </Row>

        <Row label={t('task.field.tags')}>
          <TagEditor task={task} handlers={handlers} />
        </Row>
      </DetailBox>

      {/**
       * ┌───────────────────────────────────────────────────────────────────────────┐
       * │ ⚠️⚠️ A BOX OF ITS OWN, AND THE PERSON RAIL IS THE PATTERN — item 1, 2nd.  │
       * └───────────────────────────────────────────────────────────────────────────┘
       *
       * 14 Sep moved the metafields out of the main column and into the rail, which was right, and
       * then folded them INTO the properties box under a hairline heading, which was not: it
       * invented a third thing — a box with two sections — where the product already had an
       * answer. `person-screen.tsx` has drawn a separate *Metafieldek* box beside its details box
       * since item 51, same `DetailBox`, same `DetailRow`, same 92px label column.
       *
       * ⚠️ **`inline` is what makes the two identical**, and item 51's second pass is where that
       * was settled: *"I want metafields to look exactly like details above"*. Without it the form
       * draws labelled form fields and the rail has two visual languages in one column.
       *
       * ⚠️ **Nothing at all when the workspace defines none** — no empty box, no heading.
       */}
      <Metafields data={data} handlers={handlers} />
    </div>
  );
}

/**
 * ⚠️ **The shared aligned row**, so the task rail and the person rail cannot drift apart. It used
 * to be a local `<dt>/<dd>` pair stacked vertically — see the note on the box above.
 */
function Row({ label, children }: { readonly label: string; readonly children: React.ReactNode }) {
  return <DetailRow label={label}>{children}</DetailRow>;
}

/** J-1 · one of the tracker's lists as chips, or nothing at all when it is empty. */
function TrackerRow({
  label,
  names,
}: {
  readonly label: string;
  readonly names: readonly string[];
}) {
  if (names.length === 0) return null;
  return (
    <Row label={label}>
      <span className="flex min-w-0 flex-wrap items-center gap-1 px-2">
        {/* ⚠️ 21 Oct 2026 · capped at the row and truncated — a Jira fix version like *"3. Fázis -
            Harmadik fejlesztési szakasz"* ran out of the card. The full name on hover, the kit's. */}
        {names.map((name) => (
          <Badge key={name} variant="secondary" className="max-w-full min-w-0">
            <TruncatedText>{name}</TruncatedText>
          </Badge>
        ))}
      </span>
    </Row>
  );
}

/**
 * ⚠️ **The estimate is a TEXT field that accepts `8`, `8h`, `8:00` and `8,0`**, and it commits on
 * Enter or on the ✓ — not on blur, per the inline-edit contract. It renders `H:MM`, so what is read
 * back is the one format the whole product uses.
 *
 * ⚠️ **Its draft is its own string.** Deriving it from `formatHours(task.estimateHours)` on every
 * keystroke would rewrite `8` to `8:00` while somebody is still typing `8,5`.
 */
function EstimateField({
  task,
  handlers,
}: {
  readonly task: Task;
  readonly handlers: TaskDetailHandlers;
}) {
  const t = useT();
  /* 3 Nov 2026 · shown and typed in the reader's unit, saved as hours (`decisions.md` DU-1). */
  const { fmt, fmtNum: readerNum } = useDuration();
  /* DU-2 · the open box's own unit, from its picker; closed, it starts on the reader's again. */
  const [unit, setUnit] = useState<DurationUnit | undefined>(undefined);
  const field = useDurationIn(unit);
  const { parse } = field;
  const [draft, setDraft] = useState<string | null>(null);

  if (draft === null) {
    return (
      <button
        type="button"
        disabled={!handlers.canEdit}
        className="hover:bg-accent/50 w-full rounded-md px-2 py-1 text-left tabular-nums"
        onClick={() => {
          setUnit(undefined);
          setDraft(readerNum(task.estimateHours));
        }}
      >
        {fmt(task.estimateHours)}
      </button>
    );
  }

  const commit = (): void => {
    /* Hours on the wire; text the parser refuses goes as typed, so the server's refusal names it. */
    handlers.onPatch({ estimateHours: parse(draft) ?? draft });
    setDraft(null);
  };

  return (
    <div className="flex items-center gap-1.5">
      <TextInput
        value={draft}
        autoFocus
        aria-label={t('task.field.estimate')}
        title={field.t('task.field.estimateHint')}
        onChange={(event) => {
          setDraft(event.target.value);
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter') commit();
          if (event.key === 'Escape') {
            event.preventDefault();
            setDraft(null);
          }
        }}
      />
      <DurationUnitPicker
        unit={field.unit}
        disabled={!handlers.canEdit}
        onChange={(next) => {
          setDraft(field.retype(draft, next, task.estimateHours));
          setUnit(next);
        }}
      />
    </div>
  );
}

/**
 * ⚠️ **A tag is added on Enter and removed by its own ✕.** Backspace on an empty input removes the
 * last one, which is what the mockup's `removeLastLabel` does and what everybody expects from a
 * chip input.
 */
/**
 * J-6 · what the tracker calls its labels, for a task with an issue link; null until asked, and
 * null for a task with no link, which is never asked.
 *
 * ⚠️ **Not fatal**, like the tracker filters: without it the picker offers nothing and says so,
 * and the chips still remove.
 */
function useTrackerLabels(task: Task): readonly TrackerLabel[] | null {
  const session = useSession();
  const [labels, setLabels] = useState<readonly TrackerLabel[] | null>(null);
  const linked = task.tracker !== null;
  useEffect(() => {
    if (!linked) return;
    const alive = { current: true };
    void (async () => {
      const response = await session
        .request(`/tasks/${encodeURIComponent(task.id)}/tracker-labels`)
        .catch(() => null);
      if (response?.ok !== true) return;
      const body = (await response.json().catch(() => null)) as { data?: unknown } | null;
      const parsed = trackerLabelSchema.array().safeParse(body?.data);
      if (alive.current && parsed.success) setLabels(parsed.data);
    })();
    return () => {
      alive.current = false;
    };
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- `session` is a fresh object every
       render (`preferences-boot.tsx`); the task id is what identifies the read. */
  }, [task.id, linked]);
  return linked ? (labels ?? []) : null;
}

function TagEditor({
  task,
  handlers,
}: {
  readonly task: Task;
  readonly handlers: TaskDetailHandlers;
}) {
  const t = useT();
  const [draft, setDraft] = useState('');
  const trackerLabels = useTrackerLabels(task);

  /* ⚠️ The field FIRST and the tags under it (13 Oct 2026): a tag added above the input pushed the
     field down a line each time, so the place to type kept moving away from the cursor. */
  return (
    <div className="flex flex-col gap-1">
      {/**
       * ⚠️⚠️ **J-6 · ON A LINKED TASK THE TAGS ARE THE TRACKER'S LABELS, SO THEY ARE PICKED, NOT
       * TYPED.** A label typed here cannot be created over there — Jira would take a new name and
       * YouTrack would refuse it, and the two would then disagree — so the only honest control is
       * a picker over the tracker's own list, in the tracker's spelling. An unlinked task keeps the
       * free-text field below, folded to lower case, exactly as before.
       */}
      {handlers.canEdit && trackerLabels !== null ? (
        trackerLabels.length === 0 ? (
          <span className="text-muted-foreground px-2 text-[11.5px]">
            {t('task.field.tagsTrackerNone')}
          </span>
        ) : (
          <Select
            /* 28 Oct 2026 · a tracker's label list runs long, so it searches — like the assignee. */
            searchable
            searchPlaceholder={t('list.searchOptions')}
            value=""
            aria-label={t('task.field.tagsTrackerPick')}
            className={INLINE_INPUT}
            options={[
              { value: '', label: t('task.field.tagsTrackerPick') },
              ...trackerLabels
                .filter((label) => !task.tags.includes(label.name))
                .map((label) => ({ value: label.name, label: label.name })),
            ]}
            onChange={(event) => {
              const name = event.target.value;
              if (name !== '' && !task.tags.includes(name)) {
                handlers.onPatch({ tags: [...task.tags, name] });
              }
            }}
          />
        )
      ) : null}
      {handlers.canEdit && trackerLabels === null ? (
        <TextInput
          value={draft}
          /* ⚠️ The rail's inline input — the column's full width, no box until hover or focus —
             rather than a fixed 110px field with a border at rest (13 Oct 2026). */
          className={INLINE_INPUT}
          placeholder={t('task.field.tagsPlaceholder')}
          aria-label={t('task.field.tags')}
          onChange={(event) => {
            setDraft(event.target.value);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && draft.trim() !== '') {
              /* Folded here as well as on the server, so the chip that appears is the chip that
               * was stored rather than the one that was typed. */
              const tag = draft.trim().toLowerCase();
              if (!task.tags.includes(tag)) handlers.onPatch({ tags: [...task.tags, tag] });
              setDraft('');
            }
            if (event.key === 'Backspace' && draft === '' && task.tags.length > 0) {
              handlers.onPatch({ tags: task.tags.slice(0, -1) });
            }
          }}
        />
      ) : null}
      {task.tags.length === 0 ? null : (
        <div className="flex flex-wrap items-center gap-1 px-2">
          {task.tags.map((tag) => (
            <Badge key={tag} variant="secondary">
              {tag}
              {handlers.canEdit ? (
                <button
                  type="button"
                  className="ml-1"
                  aria-label={t('task.action.removeTag')}
                  onClick={() => {
                    handlers.onPatch({ tags: task.tags.filter((candidate) => candidate !== tag) });
                  }}
                >
                  <Icon name="x" size={11} />
                </button>
              ) : null}
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ C10's metafields, on the task form.                                       │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **VALIDATED LOCALLY WITH THE SERVER'S OWN FUNCTION, BEFORE SENDING.** `validateValues` is
 * the same code the API applies — so a refusal appears beside the input that caused it, rather than
 * arriving as a 400 after the request. Two implementations of "is this a valid date for this field"
 * across nine types and five entities is forty-five chances to disagree.
 *
 * ⚠️ **The draft is local and commits on SAVE**, not on every keystroke. A metafield form is a
 * form: sending a `PATCH` per character would be a request per character, and a required field would
 * be refused while somebody was still typing into it.
 */
function Metafields({
  data,
  handlers,
}: {
  readonly data: TaskDetailData;
  readonly handlers: TaskDetailHandlers;
}) {
  const t = useT();

  if (data.metafields.length === 0) return null;

  return (
    <DetailBox title={t('task.metafields')}>
      {/**
       * ┌───────────────────────────────────────────────────────────────────────┐
       * │ ⚠️⚠️ SAVED ON CHANGE, LIKE EVERY OTHER PROPERTY BESIDE IT.            │
       * └───────────────────────────────────────────────────────────────────────┘
       *
       * The old form held a draft and committed it behind a Save button — the only thing on the
       * task screen that had to be committed, when the assignee, the status, the dates and the
       * tags directly above it all write as you set them. Two habits on one rail is one too many,
       * and the one people learn is whichever they met first.
       *
       * ⚠️⚠️ **THE VALIDATION MOVED WITH IT, AND THAT IS THE REAL COST.** A draft could be checked
       * before it was sent; a change that saves itself cannot be. A value that fails — a required
       * field cleared, a number that is not one — is refused by the SERVER now, which is where it
       * was always enforced anyway (`metafield-form` validated for the button's sake). The refusal
       * surfaces the way every other patch's does, through the screen's own error handling.
       *
       * ⚠️ **The whole map is sent, not the one key.** `onPatch({ metafields })` replaces the
       * object, so sending a single field would clear the rest — the same complete-map rule the
       * role editor follows, for the same reason.
       */}
      <MetafieldForm
        inline
        fields={data.metafields}
        values={data.task.metafields}
        errors={{}}
        /* The `user` type picks a member; the detail already has the assignee list. */
        members={data.assignees}
        disabled={!handlers.canEdit}
        onChange={(key, value: MetafieldValue) => {
          handlers.onPatch({ metafields: { ...data.task.metafields, [key]: value } });
        }}
      />
    </DetailBox>
  );
}

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ THE TICKET'S OWN FOLDER — `0115`, and the mockup drew it all along.  │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * A task had nowhere to put a file. The only way to attach a specification was to comment with
 * it, which buries the thing everybody needs inside a conversation — and a comment's attachment
 * goes when the comment goes.
 *
 * ⚠️ **The same `Attachments` every other subject uses**, against the new `task` target. A second
 * implementation would be a second place for the upload rules, the type sniffing and the
 * reveal-once behaviour to drift.
 *
 * ⚠️⚠️ **ONE ROW, LIKE *KAPCSOLÓDÓ FELADATOK* — feedback round 4 item 12.** The section used to
 * open with `DropZone`'s dashed box: an icon, a prompt, a sentence of accepted formats and a browse
 * button, five lines tall, above a list that is usually empty. It was the tallest block on the page
 * and it was mostly instructions. `attachments.tsx` argues the compact mode; what it costs here is
 * the `<h3>` and the `<section>` padding, because the component draws its own heading now.
 *
 * ⚠️ **Dropping still works** — the whole section is the target, which is larger than the box
 * was. The earlier note that the strip existed because *"the only way to attach anything was to
 * drag a file onto the page, and that is a gesture nobody discovers"* is answered by the button
 * rather than by the box.
 */
function TaskAttachments({
  data,
  handlers,
  uploads,
}: {
  readonly data: TaskDetailData;
  readonly handlers: TaskDetailHandlers;
  /** ⚠️ Bumped by a drop on the surface — the list has no other way to hear about one. */
  readonly uploads: number;
}) {
  const t = useT();

  return (
    /**
     * ⚠️⚠️ **NO CARD — IT IS THE THIRD BLOCK IN A COLUMN OF THREE.** *Függőségek* and
     * *Kapcsolódó feladatok* are a heading with an action on its row and then plain rows; this one
     * was the same content inside a bordered card, so a column meant to read as one pattern read
     * as two. The border is what made it the odd one out, and the attachments now arrive as a new
     * row exactly as a dependency or a link does.
     */
    <div>
      <Attachments
        /* ⚠️ Keyed on the upload counter, so a file dropped on the surface reloads this list. */
        key={uploads}
        target="task"
        targetId={data.task.id}
        canEdit={handlers.canEdit}
        /* ⚠️ The heading is what puts the component in its compact mode — see the note above. */
        heading={t('task.attachments')}
        emptyNote={t('task.attachments.empty')}
        onUpload={handlers.onAttachFiles}
        /**
         * ┌───────────────────────────────────────────────────────────────────────────┐
         * │ ⚠️⚠️ ONE FILE, TWO PLACES THAT MENTION IT — 15 Sep.                        │
         * └───────────────────────────────────────────────────────────────────────────┘
         *
         * A file dropped on the description is attached to the task AND linked from its text. The
         * first report of the link feature was a 404, and the audit trail said why: the file had
         * been attached at 11:17:18 and deleted from this list at 11:18:54. Nothing was wrong with
         * the link; the file it named was gone, and the text still said otherwise.
         *
         * ⚠️ **So deleting is a question when the text refers to it**, and answering yes takes the
         * link out too. Leaving a dead link is worse — it reads as the product being broken rather
         * than as a thing somebody did — and refusing the delete would be the list telling somebody
         * they may not remove their own file.
         */
        removalNote={(attachment) =>
          linksTo(data.task.description, attachment.id)
            ? t('task.attachment.linkedInDescription')
            : null
        }
        onRemoved={(attachment) => {
          const next = withoutLink(data.task.description, attachment.id);
          if (next !== data.task.description) handlers.onPatch({ description: next });
        }}
      />
    </div>
  );
}

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ WHETHER A DESCRIPTION MENTIONS A FILE, AND TAKING THE MENTION OUT.   │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **PARSED, NOT REGEXED.** The obvious version is `html.replace(/<a[^>]*files\/id[^>]*>.*?<\/a>/)`
 * and it is wrong in the two ways HTML regexes are always wrong: an attribute containing `>` ends
 * the match early, and a lazy `.*?` stops at the first `</a>` rather than the matching one. The
 * browser has a parser; this runs in the browser.
 *
 * ⚠️ **It matches on `/files/<id>` rather than on the whole href**, because the href is workspace-
 * scoped and this component does not know the workspace. The id is a uuid v7 — it cannot collide
 * with another file, and a description that merely mentions the id as text has no anchor to find.
 *
 * ⚠️ **An emptied paragraph goes with the link.** `<p><a>…</a></p>` is what the drop writes, so
 * removing only the anchor would leave a blank line nobody can select or delete — the same failure
 * the description's own empty check exists to prevent.
 */
function parseDescription(html: string | null): Document | null {
  if (html === null || html === '') return null;
  return new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html');
}

function linksTo(html: string | null, fileId: string): boolean {
  const parsed = parseDescription(html);
  return parsed !== null && parsed.querySelector(`a[href*="/files/${fileId}"]`) !== null;
}

function withoutLink(html: string | null, fileId: string): string | null {
  const parsed = parseDescription(html);
  if (parsed === null) return html;

  for (const anchor of parsed.querySelectorAll(`a[href*="/files/${fileId}"]`)) {
    const paragraph = anchor.closest('p');
    anchor.remove();
    /* ⚠️ `textContent.trim()` rather than `childElementCount`: a paragraph holding only whitespace
       between two removed links is as empty as one holding nothing. */
    if (paragraph !== null && paragraph.textContent.trim() === '') paragraph.remove();
  }

  const next = parsed.body.innerHTML;
  /* ⚠️ An emptied description becomes `null`, not `''` — the field is nullable and the screen's
     own empty state reads `=== null`. */
  return next.replace(/<[^>]*>/gu, '').trim() === '' ? null : next;
}

/* ── subtasks and relations ────────────────────────────────────────────────────────────────── */

/**
 * Alfeladatok — `main.jsx` 2738–2756.
 *
 * ⚠️⚠️ **IT RENDERS WHEN THERE ARE NONE, AND THAT IS THE WHOLE REASON THIS LOOKED UNBUILT.** The
 * section used to `return null` on an empty list, so a task with no subtasks had no heading, no
 * button and nothing to say the feature existed. The composer is the affordance; hiding it until
 * somebody has already used it is a door that only opens from inside.
 *
 * ⚠️⚠️ **NO CHECKBOX, WHICH IS A DELIBERATE DEPARTURE FROM THE MOCKUP.** Its rows tick to
 * `st: 'done'` and untick to `'progress'`, which works because it has five hardcoded statuses.
 * Ours are per-workspace rows an admin renames, reorders and adds to — a workspace with *Kész*,
 * *Elutasítva* and *Duplikátum* has three `closed` statuses, and a tick-box choosing among them
 * would be making an editorial decision nobody asked for. Untick is worse: nothing records which
 * status it came from, and the one `is_default` row is an OPEN status, so unticking would demote
 * work that was in progress. The row shows its status the way every other task row in the product
 * does, and the drawer is one click away with the real menu in it.
 *
 * ⚠️ **Removing a subtask DETACHES it** — see `detachSubtask`. *"Önálló jegy lett"*.
 */
function Subtasks({
  data,
  handlers,
}: {
  readonly data: TaskDetailData;
  readonly handlers: TaskDetailHandlers;
}) {
  const t = useT();
  const [draft, setDraft] = useState('');

  const done = data.subtasks.filter((task) => task.statusCategory === 'closed').length;

  const submit = (): void => {
    const title = draft.trim();
    if (title === '') return;
    handlers.onCreateSubtask(title);
    setDraft('');
  };

  return (
    <section className="mt-6" data-tour="task-subtasks">
      <h3 className="mb-2 flex items-center gap-2 text-[13px] font-medium">
        {t('task.subtasks')}
        {/* ⚠️ The count only when there is something to count — "0 / 0 kész" is noise. */}
        {data.subtasks.length === 0 ? null : (
          <span className="text-muted-foreground text-[11.5px] tabular-nums">
            {t('task.subtasks.progress', { done, total: data.subtasks.length })}
          </span>
        )}
      </h3>

      <ul className="flex flex-col gap-1">
        {data.subtasks.map((subtask) => (
          <li key={subtask.id} className="group flex items-center gap-1">
            <button
              type="button"
              className="hover:bg-accent/50 flex min-w-0 flex-1 items-center gap-2 rounded-md px-2 py-1.5 text-left"
              onClick={() => {
                handlers.onOpenTask(subtask);
              }}
            >
              <span
                className="size-2 shrink-0 rounded-[3px]"
                style={{ background: subtask.statusColor }}
                aria-hidden
              />
              <span className="text-muted-foreground w-[64px] shrink-0 truncate text-[11.5px] tabular-nums">
                {subtask.key}
              </span>
              <span
                className={[
                  'min-w-0 flex-1 truncate text-[12.5px]',
                  subtask.statusCategory === 'closed' ? 'text-muted-foreground line-through' : '',
                ].join(' ')}
              >
                {subtask.title}
              </span>
              {/* The status in WORDS beside the dot — colour is never the only carrier. */}
              <span className="text-muted-foreground shrink-0 text-[11.5px]">
                {subtask.statusName}
              </span>
            </button>

            {handlers.canEdit ? (
              <Button
                variant="ghost"
                size="icon-sm"
                /* ⚠️ Named after the SUBTASK, not "detach" alone: a list of seven identical
                   "Leválasztás" buttons names nothing for a screen reader. */
                aria-label={t('task.subtasks.detachOne', { key: subtask.key })}
                onClick={() => {
                  handlers.onDetachSubtask(subtask);
                }}
              >
                <Icon name="x" size={14} />
              </Button>
            ) : null}
          </li>
        ))}
      </ul>

      {handlers.canEdit ? (
        <div className="mt-2 flex items-center gap-2">
          <TextInput
            value={draft}
            onChange={(event) => {
              setDraft(event.target.value);
            }}
            onKeyDown={(event) => {
              if (event.key !== 'Enter') return;
              event.preventDefault();
              submit();
            }}
            placeholder={t('task.subtasks.placeholder')}
            aria-label={t('task.subtasks.placeholder')}
            className="flex-1"
          />
          {/* ⚠️ Disabled on an empty draft rather than silently doing nothing on click. */}
          <Button size="sm" variant="outline" disabled={draft.trim() === ''} onClick={submit}>
            {t('task.subtasks.add')}
          </Button>
        </div>
      ) : null}

      {/* ⚠️ Said out loud, because the server enforces it and a refusal after the fact is a worse
          way to learn it. A subtask of a task with no project has no project either. */}
      {handlers.canEdit && data.task.projectName !== null ? (
        <p className="text-muted-foreground mt-1.5 text-[11.5px]">
          {t('task.subtasks.inherits', { project: data.task.projectName })}
        </p>
      ) : null}
    </section>
  );
}

/**
 * Függőségek and Kapcsolódó feladatok.
 *
 * ⚠️ **Dependencies and links render as TWO lists, not one.** The mockup concatenates them, and the
 * difference is not cosmetic: a dependency has a direction and a lag, so *"waits for HSO-131, +2
 * days"* and *"relates to HSO-142"* are different sentences. See migration 0015.
 *
 * ⚠️⚠️ **THE MOCKUP HAS NO WAY TO CREATE A DEPENDENCY AT ALL** — its `deps` are seed data, and the
 * only thing it does with them is warn on the Gantt when a drag breaks one. Our API has had the
 * endpoint since C8, so the control is ours to place; it goes beside the links because that is the
 * one screen that already knows the task. Recorded rather than left as a difference somebody
 * assumes was an oversight.
 *
 * ⚠️ **Both sections render EMPTY**, for the reason `Subtasks` gives at length.
 */
function Relations({
  data,
  handlers,
}: {
  readonly data: TaskDetailData;
  readonly handlers: TaskDetailHandlers;
}) {
  const t = useT();
  const [picking, setPicking] = useState<'link' | 'dependency' | null>(null);
  const [lag, setLag] = useState('0');

  /**
   * ⚠️⚠️ **THE TASK ITSELF AND EVERY TASK ALREADY RELATED TO IT.** The server refuses `self` and
   * `duplicate` with a `conflict`, and offering a choice that will be refused is worse than not
   * offering it. Both relation kinds share the exclusion list: the mockup's own `linkable`
   * excludes anything already on its combined list, and it is the more useful reading.
   */
  const related = [
    data.task.id,
    ...data.links.map((link) => link.taskId),
    ...data.dependencies.map((dependency) => dependency.taskId),
  ];

  /**
   * ⚠️ **ONE ELEMENT, DROPPED INTO WHICHEVER SECTION IS OPEN.** A dependency and a link are the
   * same search with a different title; two copies would be two places to fix the exclusion list.
   */
  const picker = (
    <TaskPicker
      title={picking === 'dependency' ? t('task.dependency.add') : t('task.link.add')}
      exclude={related}
      scopeHint={
        data.task.projectName === null
          ? t('task.picker.scope.none')
          : t('task.picker.scope.project', { project: data.task.projectName })
      }
      onSearch={handlers.onSearchTasks}
      /* ⚠️ The lag only exists for a dependency; a link has nothing to offset. */
      leading={
        picking !== 'dependency' ? undefined : (
          <div className="flex items-center gap-2">
            <label className="text-muted-foreground text-[11.5px]" htmlFor="dependency-lag">
              {t('task.dependency.lagLabel')}
            </label>
            <TextInput
              id="dependency-lag"
              value={lag}
              onChange={(event) => {
                setLag(event.target.value);
              }}
              className="w-[80px]"
            />
          </div>
        )
      }
      onPick={(task) => {
        if (picking === 'dependency') {
          /* A lag that is not a number is ZERO, not NaN — the schema's bounds would refuse it and
             the reader would get a validation error about a field they left as typed. */
          const days = Number.parseInt(lag, 10);
          handlers.onAddDependency(task.id, Number.isNaN(days) ? 0 : days);
        } else {
          handlers.onAddLink(task.id);
        }
        setPicking(null);
        setLag('0');
      }}
      onClose={() => {
        setPicking(null);
      }}
    />
  );

  return (
    <section className="mt-6 flex flex-col gap-4" data-tour="task-dependencies">
      <div>
        {/* ⚠️ `relative`, so the picker can anchor to this heading's button — see `TaskPicker`. */}
        <h3 className="mb-2 flex items-center gap-2 text-[13px] font-medium">
          {t('task.dependencies')}
          <div className="flex-1" />
          {handlers.canSchedule ? (
            <span className="relative">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setPicking('dependency');
                }}
              >
                <Icon name="plus" size={13} />
                {t('task.dependency.add')}
              </Button>
              {picking !== 'dependency' ? null : picker}
            </span>
          ) : null}
        </h3>

        {data.dependencies.length === 0 ? (
          <p className="text-muted-foreground text-[11.5px]">{t('task.dependency.empty')}</p>
        ) : (
          <ul className="flex flex-col gap-1">
            {data.dependencies.map((dependency) => (
              <li key={dependency.id} className="flex items-center gap-2 px-2 py-1 text-[12.5px]">
                {/* 30 Oct 2026 · fixed cells, so the key and the title line up row to row. */}
                <span className="flex w-[84px] shrink-0">
                  <Badge variant="outline">
                    {dependency.direction === 'predecessor'
                      ? t('task.dependency.waitsFor')
                      : t('task.dependency.blocks')}
                  </Badge>
                </span>
                <span className="text-muted-foreground w-[64px] shrink-0 truncate text-[11.5px] tabular-nums">
                  {dependency.key}
                </span>
                <span className="min-w-0 flex-1 truncate">{dependency.title}</span>
                {/* A lag of zero is still shown — an absent number would read as "no lag
                    defined" rather than "no lag". The mockup renders it too. */}
                <span className="text-muted-foreground w-[64px] shrink-0 text-right text-[11.5px] tabular-nums">
                  {t('task.dependency.lag', { days: dependency.lagDays })}
                </span>
                {handlers.canSchedule ? (
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label={t('task.dependency.removeOne', { key: dependency.key })}
                    onClick={() => {
                      handlers.onRemoveDependency(dependency.id);
                    }}
                  >
                    <Icon name="x" size={14} />
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div>
        <h3 className="mb-2 flex items-center gap-2 text-[13px] font-medium">
          {t('task.links')}
          <div className="flex-1" />
          {/* ⚠️ The button and its panel share a positioned wrapper, so the panel anchors to the
              BUTTON rather than to whatever happens to be positioned further up. */}
          {handlers.canEdit ? (
            <span className="relative">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setPicking('link');
                }}
              >
                <Icon name="plus" size={13} />
                {t('task.link.add')}
              </Button>
              {picking !== 'link' ? null : picker}
            </span>
          ) : null}
        </h3>

        {data.links.length === 0 ? (
          <p className="text-muted-foreground text-[11.5px]">{t('task.link.empty')}</p>
        ) : (
          <ul className="flex flex-col gap-1">
            {data.links.map((link) => (
              <li key={link.id} className="flex items-center gap-1">
                <button
                  type="button"
                  className="hover:bg-accent/50 flex min-w-0 flex-1 items-center gap-2 rounded-md px-2 py-1 text-left text-[12.5px]"
                  onClick={() => {
                    handlers.onOpenTask({ id: link.taskId, key: link.key } as Task);
                  }}
                >
                  <span
                    className="size-2 shrink-0 rounded-[3px]"
                    style={{ background: link.statusColor }}
                    aria-hidden
                  />
                  <span className="text-muted-foreground w-[64px] shrink-0 truncate text-[11.5px] tabular-nums">
                    {link.key}
                  </span>
                  <span className="min-w-0 flex-1 truncate">{link.title}</span>
                  <span className="text-muted-foreground shrink-0 text-[11.5px]">
                    {link.statusName}
                  </span>
                </button>
                {handlers.canEdit ? (
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label={t('task.link.removeOne', { key: link.key })}
                    onClick={() => {
                      handlers.onRemoveLink(link.id);
                    }}
                  >
                    <Icon name="x" size={14} />
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

/* ── activity ──────────────────────────────────────────────────────────────────────────────── */

function Comments({
  data,
  handlers,
  stagedFiles,
  onStagedTaken,
}: {
  readonly data: TaskDetailData;
  readonly handlers: TaskDetailHandlers;
  /** ⚠️ A drop that landed over the comment box — see `ScopedDropSurface`. */
  readonly stagedFiles: readonly File[] | null;
  readonly onStagedTaken: () => void;
}) {
  const t = useT();
  const avatars = useAvatars(data.comments.map((comment) => comment.authorAvatarUrl));
  /** ⚠️ Item 73 · which comment is being edited, by id. `null` is none. */
  const [editingId, setEditingId] = useState<string | null>(null);

  return (
    <div className="mt-3 flex flex-col gap-3">
      {data.comments.length === 0 ? (
        <p className="text-muted-foreground text-[12.5px]">{t('task.comments.empty')}</p>
      ) : (
        data.comments.map((comment) => (
          /**
           * ⚠⚠️ **EACH COMMENT IN ITS OWN CARD.** They were bare `article`s separated by a gap,
           * and a thread of them read as one column of undifferentiated text — reported. The
           * mockup does not draw this surface, so `design-system/readme.md` decides it: `--card`,
           * a 1px hairline, 10px radius, NO shadow. Shadow means "this floats".
           */
          <article key={comment.id} className="bg-card flex flex-col gap-1 rounded-lg border p-3">
            <header className="flex items-center gap-2">
              {/* ⚠️ The face, which the mockup's own comment carries (`ini` and `color` in
                  `main.jsx`) and this never drew. It is most of what makes a thread scannable. */}
              <Avatar
                fallback={commentInitials(comment.authorName)}
                seed={comment.authorAvatarSeed ?? undefined}
                title={comment.authorName}
                size={22}
                src={avatars.get(comment.authorAvatarUrl ?? '')}
              />
              <span className="text-[12.5px] font-medium">{comment.authorName}</span>
              <span className="text-muted-foreground text-[11px]">
                {comment.createdAt.slice(0, 16).replace('T', ' ')}
              </span>
              {comment.editedAt === null ? null : (
                <span className="text-muted-foreground text-[11px]">
                  {t('task.comment.edited')}
                </span>
              )}

              {/**
               * ⚠️⚠️ **ITEM 73 · ONLY ON YOUR OWN COMMENT, because that is the server's rule.**
               * `relation.service.ts` throws `NotYourCommentError` for anybody else's, so a
               * button offered on a colleague's would always end in a refusal — which teaches
               * people the product is broken rather than that the rule exists.
               *
               * ⚠️ **`canEdit` gates it too.** Somebody who has lost `task.update` cannot write
               * to the task at all, their own comment included.
               */}
              {handlers.canEdit &&
              data.currentMemberId !== undefined &&
              data.currentMemberId !== null &&
              comment.authorMemberId === data.currentMemberId &&
              editingId === null ? (
                <>
                  <div className="flex-1" />
                  {handlers.onEditComment === undefined ? null : (
                    <button
                      type="button"
                      className="text-muted-foreground hover:text-foreground text-[11px]"
                      onClick={() => {
                        setEditingId(comment.id);
                      }}
                    >
                      {t('common.edit')}
                    </button>
                  )}
                  {handlers.onDeleteComment === undefined ? null : (
                    <button
                      type="button"
                      /* ⚠️ Destructive COLOUR, not a filled danger button: the design system
                         reserves that for irreversible financial deletions. */
                      className="text-muted-foreground hover:text-destructive text-[11px]"
                      onClick={() => {
                        handlers.onDeleteComment?.(comment);
                      }}
                    >
                      {t('common.delete')}
                    </button>
                  )}
                </>
              ) : null}
            </header>
            {/**
             * ⚠️⚠️ **`dangerouslySetInnerHTML`, and it is safe for exactly one reason: the server
             * sanitised this on the way IN.** `relation.service.ts` runs an allow-list at write and
             * drops every attribute, so the column can be trusted. Sanitising here instead would
             * mean doing it at every render site, and the first one somebody forgets is the hole.
             *
             * If that write-side guarantee ever weakens, this line becomes stored XSS — which is
             * why the sanitiser has its own tests naming the attacks it stops.
             */}
            {editingId === comment.id ? (
              /* ⚠️ **The same composer**, so an edit can add or remove an `@` — which is what the
                 server notifies on. `comment-composer.tsx` carries the argument. */
              <CommentComposer
                people={data.people}
                initialHtml={comment.bodyHtml}
                submitLabel={t('common.save')}
                allowFiles={false}
                onCancel={() => {
                  setEditingId(null);
                }}
                onSubmit={(html, mentions) => {
                  handlers.onEditComment?.(comment, html, mentions);
                  setEditingId(null);
                }}
              />
            ) : (
              <div
                className={`text-[13px] leading-relaxed ${COMMENT_PROSE}`}
                dangerouslySetInnerHTML={{ __html: highlightMentions(comment) }}
              />
            )}
            {/* ⚠️ C22 · BESIDE the comment, never inside its markup. R-18 named allowing `img` and
                `src` as the hazard; a list alongside means `comment-markup.ts` keeps its closed tag
                set and `ALLOWED_URI_REGEXP` is never widened. */}
            <Attachments target="task_comment" targetId={comment.id} canEdit={handlers.canEdit} />
          </article>
        ))
      )}

      {handlers.canEdit ? (
        <div {...dropZone('comment')} data-tour="task-comment">
          <CommentComposer
            people={data.people}
            onSubmit={handlers.onAddComment}
            incomingFiles={stagedFiles}
            onIncomingTaken={onStagedTaken}
          />
        </div>
      ) : null}
    </div>
  );
}

/**
 * Two initials for a comment's author.
 *
 * ⚠️ **First and last word, which is the rule every other avatar in this product uses** — a fourth
 * variant of "how do we shorten a name" is how one screen ends up saying `TG` and another `Ta`.
 */
function commentInitials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  const first = words[0]?.[0] ?? '';
  const last = words.length > 1 ? (words[words.length - 1]?.[0] ?? '') : '';
  return `${first}${last}`.toUpperCase();
}
